<template>
    <div>
        <Sidebar></Sidebar>

        <div class="content-wrapper">
            <div class="img-desktop img-desktop-control d-none d-lg-inline-block"></div>

            <section class="content">
                <div class="container-fluid">
                    <div class="row">
                        <ButtonSpinner />
                        <div class="mb-4">
                            <h1 class="inline-input">Recherche de dispenses</h1>
                        </div>
                    </div>

                    <div class="row">
                        <Fieldset title="Afficher les filtres" title-collapse="Masquer les filtres">
                            <div class="filters">
                                <div class="filter-entry">
                                    <label for="enterprise">Entreprise</label>
                                    <div class="input">
                                        <Select id="enterprise" v-model="draftFilters.enterprise">
                                            <option value="" />
                                            <option v-for="(name, id) in enterprises" :key="id" :value="id">
                                                {{ name }}
                                            </option>
                                        </Select>
                                    </div>
                                </div>
                                <div class="filter-entry">
                                  <label for="employeeName">Employé</label>
                                  <div class="input">
                                    <input class="form-control flat" id="employeeName" v-model="draftFilters.employeeName" />
                                  </div>
                                </div>
                                <div class="filter-entry">
                                    <label for="status">Statut</label>
                                    <div class="input">
                                        <Select id="status" multiple v-model="draftFilters.status">
                                            <option v-for="(name, code) in statuses" :key="code" :value="code">
                                                {{ name }}
                                            </option>
                                        </Select>
                                    </div>
                                </div>
                                <div v-for="dateRangeFilter in dateRangeFilters" :key="dateRangeFilter.fromKey" class="filter-entry">
                                    <label :for="dateRangeFilter.fromKey" :title="dateRangeFilter.title">{{ dateRangeFilter.label }}</label>
                                    <div class="input input--date-range">
                                        <div class="date-range-field">
                                            <span class="date-range-separator">entre le</span>
                                            <DatePicker :id="dateRangeFilter.fromKey" v-model="draftFilters[dateRangeFilter.fromKey]" />
                                        </div>
                                        <div class="date-range-field">
                                            <span class="date-range-separator">et le</span>
                                            <DatePicker :id="dateRangeFilter.toKey" v-model="draftFilters[dateRangeFilter.toKey]" />
                                        </div>
                                    </div>
                                </div>
                                <div class="filter-buttons-wrapper">
                                    <button
                                        class="nav-button btn btn-primary"
                                        :disabled="isLoading"
                                        @click.prevent="submitFilters"
                                    >
                                        Filtrer
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-secondary"
                                        :disabled="!Object.values(draftFilters).some(v => v !== null)"
                                        @click.prevent="resetFilters(true)"
                                    >
                                        Réinitialiser les filtres
                                    </button>
                                </div>
                            </div>
                        </Fieldset>
                    </div>

                    <SmoothExpand>
                        <div class="row justify-content-between table-pre-header">
                            <div class="stats">
                                <template v-if="pagination.totalItems > 0">
                                    <button :disabled="page === 1" class="nav-button btn btn-primary" @click.prevent="goToPrevious">&lt;</button>
                                    <button :disabled="page === pagesCount" class="nav-button btn btn-primary" @click.prevent="goToNext">&gt;</button>
                                    Demandes de dispense <b>{{ numStart }}</b> <template v-if="numStart !== numEnd">à <b>{{ numEnd }}</b></template> sur <b>{{ pagination.totalItems }}.</b>
                                </template>
                                <ButtonSpinner :is-loading="isLoading" />
                            </div>
                            <div class="submit-buttons-wrapper" v-if="pagination.totalItems > 0 && isCanViewExemptions">
                                <button
                                    type="button"
                                    class="btn btn-primary"
                                    :title="hasSelection ? '' : 'Sélectionnez au moins une demande de dispense'"
                                    :disabled="!hasSelection || isExportingList"
                                    @click.prevent="downloadListExport()"
                                >
                                    Exporter la liste <ButtonSpinner :is-loading="isExportingList" />
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-primary"
                                    :disabled="!hasSelection || isExportingListWithAttachments"
                                    :title="hasSelection ? '' : 'Sélectionnez au moins une demande de dispense'"
                                    @click.prevent="downloadListExport(true)"
                                >
                                    Exporter la liste avec les pièces jointes <ButtonSpinner :is-loading="isExportingListWithAttachments" />
                                </button>
                            </div>
                        </div>
                    </SmoothExpand>

                    <div class="row">
                        <div class="col afflex-table-content">
                            <div v-if="pagination.totalItems > 0" class="select-all">
                                <PrettyCheck id="select-all" v-model="selectAll" :class="{ mixed: isPartialSelection }" />
                            </div>
                            <vuetable
                                ref="vuetable"
                                data-path="data"
                                pagination-path="pagination"
                                :http-fetch="fetchExemptions"
                                no-data-template="Aucune donnée disponible"
                                class="table table-striped table-bordered"
                                :fields="exemptionFields"
                            >
                                <template v-slot:checkbox="props">
                                    <PrettyCheck
                                        :checked="exemptionSelection[props.rowData.id] || false"
                                        @change="checked => updateSelection(props.rowData.id, checked)"
                                    />
                                </template>
                                <template v-slot:startDate="props">
                                    <div>{{ props.rowData.startDate | formatDate }}</div>
                                </template>
                                <template v-slot:expirationDate="props">
                                    <div>{{ props.rowData.exemptionReason && props.rowData.exemptionReason.expirationDate | formatDate }}</div>
                                </template>

                                <template v-slot:status="props">
                                    <div>{{ getExemptionStatusLabel(props.rowData.status) }}</div>
                                </template>

                                <template v-slot:actions="props">
                                    <select v-if="isExportableStatus(props.rowData.status) || (isCanEditExemptions && props.rowData.status !== 'archived')"
                                            class="custom-select"
                                            v-model="selectValue"
                                            @change="handleSelect($event, props.rowData)"
                                    >
                                        <option disabled selected value="default">Action</option>
                                        <option v-if="isExportableStatus(props.rowData.status)" value="export">Exporter</option>
                                        <option v-if="props.rowData.status === 'validated'" value="earlyTermination">Fin anticipée</option>
                                        <option v-if="isCanCreateExemptions && props.rowData.status === 'validated'" value="abort">Annuler</option>
                                        <option v-if="isCanEditExemptions && props.rowData.status !== 'archived'" value="updatePersonalData">Modifier</option>
                                    </select>
                                </template>
                            </vuetable>

                            <Paginator
                                v-if="pagesCount > 0"
                                :pagination.sync="pagination"
                                class="float-right"
                            />
                        </div>
                    </div>

                    <ExemptionListExportRequestsList />
                </div>
            </section>
        </div>

        <Footer></Footer>

        <ExemptionEarlyTermination
            :showModal.sync="showEarlyTerminationModal"
            :exemption="earlyTerminationExemption"
            @terminateEarlySuccess="$refs.vuetable.refresh()"
        />

        <ExemptionEvaluation
            :modalTitle="'Annuler la dispense'"
            :fieldName="'Motif d\'annulation'"
            :isAbort="true"
            :showModal.sync="showAbortModal"
            :exemption.sync="abortExemption"
            v-on:abortSuccess="handleAbortSuccess"
        ></ExemptionEvaluation>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import Vuetable from 'vuetable-2';
    import { debounce } from 'lodash';
    import PrettyCheck from 'pretty-checkbox-vue/check';

    import ExemptionListExportRequestsList from './ExemptionListExportRequestsList'
    import Sidebar from '../../components/Sidebar';
    import Footer from '../../components/Footer';
    import ButtonSpinner from '../../components/ButtonSpinner';
    import Paginator from '../../components/Paginator';
    import PaginationMixin from '../../mixins/paginationMixin';
    import { formatDate } from '@/utils/filters';
    import { getExemptionStatuses, getExemptionStatusLabel } from '@/utils/utils';
    import {
      downloadExemptionExportFileById,
      fetchReviewedExemptions,
      requireListExemptionsExport
    } from '@/service/api/exemptions.api';
    import permissionManager from '@/service/manager/permissionManager';
    import { fetchEnterprisesIdAndNames } from '@/service/api/enterprise.api';
    import Fieldset from '@/components/Fieldset.vue';
    import Select from '@/components/form-field/Select.vue';
    import DatePicker from '@/components/form-field/DatePicker.vue';
    import SmoothExpand from '@/components/SmoothExpand.vue';
    import { messageTexts, successMessage } from '@/utils/sweet-alert-options';
    import ExemptionEarlyTermination from '@/components/modal/ExemptionEarlyTermination';
    import ExemptionEvaluation from '@/components/modal/ExemptionEvaluation';

    export default {
        fetchController: null,
        data() {
            return {
                exemptions: [],
                enterprises: {},
                statuses: getExemptionStatuses(),
                dateRangeFilters: [
                    {
                        label: 'Date de création',
                        title: 'Filtre sur la  date de création de la demande',
                        fromKey: 'createdAtFrom',
                        toKey: 'createdAtTo'
                    },
                    {
                        label: 'Date de soumission',
                        title: "Filtre sur la date de soumission par l'employé (passage au statut En attente de validation)",
                        fromKey: 'submittedAtFrom',
                        toKey: 'submittedAtTo'
                    },
                    {
                        label: 'Date de validation',
                        title: 'Filtre sur la date de validation de la demande',
                        fromKey: 'validatedAtFrom',
                        toKey: 'validatedAtTo'
                    },
                    {
                        label: 'Date de refus',
                        title: 'Filtre sur la date de refus de la demande',
                        fromKey: 'rejectedAtFrom',
                        toKey: 'rejectedAtTo'
                    },
                    {
                        label: 'Date de début',
                        title: 'Filtre sur la date de début de la dispense',
                        fromKey: 'startDateFrom',
                        toKey: 'startDateTo'
                    },
                    {
                        label: 'Date de fin',
                        title: "Filtre sur la date de fin de la dispense",
                        fromKey: 'endDateFrom',
                        toKey: 'endDateTo'
                    },
                ],
                exportIsLoading: false,
                sortOrder: [{ sortField: 'exemption.createdAt', 'direction': 'asc' }],
                draftFilters: {
                    enterprise: null,
                    employeeName: null,
                    createdAtFrom: null,
                    createdAtTo: null,
                    status: null,
                    submittedAtFrom: null,
                    submittedAtTo: null,
                    validatedAtFrom: null,
                    validatedAtTo: null,
                    rejectedAtFrom: null,
                    rejectedAtTo: null,
                    startDateFrom: null,
                    startDateTo: null,
                    endDateFrom: null,
                    endDateTo: null
                },
                liveFilters: {},
                loading: false,
                exportingList: false,
                exportingListWithAttachments: false,
                exemptionSelection: {},
                selectAll: false,
                exportRequests: [],
                showEarlyTerminationModal: false,
                earlyTerminationExemption: null,
                showAbortModal: false,
                abortExemption: null,
                selectValue: 'default',
            };
        },
        computed: {
            ...mapGetters({
                exportRequestId: 'exportRequestId',
                userPermissions: 'userPermissions'
            }),
            isCanViewExemptions() {
                return permissionManager.isCanViewExemptions(this.userPermissions);
            },
            isCanEditExemptions() {
                return permissionManager.isCanEditExemptions(this.userPermissions);
            },
            isCanCreateExemptions() {
                return permissionManager.isCanCreateExemptions(this.userPermissions);
            },
            exemptionFields() {
                return [
                    {
                        name: 'checkbox',
                        title: '',
                        width: '20px',
                        titleClass: 'center',
                        dataClass: 'center',
                    },
                    {
                        name: 'enterprise.name',
                        sortField: 'enterprise.name',
                        title: 'Entreprise',
                    },
                    {
                        name: 'lastName',
                        sortField: 'exemption.lastName',
                        title: 'Nom',
                        width: '16%'
                    },
                    {
                        name: 'firstName',
                        sortField: 'exemption.firstName',
                        title: 'Prénom',
                        width: '13%'
                    },
                    {
                        name: 'createdAt',
                        title: 'Date de création',
                        sortField: 'exemption.createdAt',
                        titleClass: 'text-center',
                        dataClass: 'text-center',
                        width: '9%',
                        formatter: value => formatDate(value)
                    },
                    {
                        name: 'startDate',
                        title: 'Date de début',
                        titleClass: 'text-center',
                        dataClass: 'text-center',
                        width: '9%',
                    },
                    {
                        name: 'expirationDate',
                        title: 'Date de fin',
                        titleClass: 'text-center',
                        dataClass: 'text-center',
                        width: '9%',
                    },
                    {
                        name: 'status',
                        title: 'Etat',
                        sortField: 'exemption.status',
                        width: '10%'
                    },
                    {
                        name: 'actions',
                        title: 'Actions',
                        titleClass: 'text-center',
                        dataClass: 'text-center',
                        width: '10%'
                    }
                ];
            },
            numStart() {
                return (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1;
            },
            numEnd() {
                return Math.min(this.pagination.totalItems, this.pagination.currentPage * this.pagination.itemsPerPage);
            },
            isLoading() {
                return this.loading;
            },
            isExportingList() {
                return this.exportingList;
            },
            isExportingListWithAttachments() {
                return this.exportingListWithAttachments;
            },
            hasSelection() {
                if (this.pagination.totalItems === 0) {
                    return false;
                }

                if (this.selectAll) {
                    return Object.values(this.exemptionSelection).filter(selected => !selected).length < this.pagination.totalItems;
                }

                return Object.values(this.exemptionSelection).some(selected => selected);
            },
            isPartialSelection() {
                if (!this.selectAll) {
                    return false;
                }

                return Object.values(this.exemptionSelection).some(selected => !selected);
            }
        },
        watch: {
            page(page) {
                this.$refs.vuetable.gotoPage(page);
                this.autoCheckExemptions(false);
            },
            liveFilters: {
                handler() {
                    this.debouncedOnFiltersChange();
                },
                deep: true
            },
            exemptions() {
                this.autoCheckExemptions(false);
            },
            selectAll(isChecked) {
                if (isChecked) {
                    this.autoCheckExemptions(true);
                } else {
                    this.exemptionSelection = {};
                }
            },
            exemptionSelection(newSelection) {
                if (Object.values(newSelection).filter(selected => !selected).length === this.pagination.totalItems) {
                    this.selectAll = false;
                } else if (Object.values(newSelection).filter(selected => selected).length === this.pagination.totalItems) {
                    this.selectAll = true;
                }
            }
        },
        created() {
            this.debouncedOnFiltersChange = debounce(this.onFiltersChange, 300);
            this.fetchEnterprises();
        },
        methods: {
            getExemptionStatusLabel,
            isExportableStatus(status) {
                return ['validated', 'rejected', 'aborted', 'archived', 'terminated_early'].includes(status);
            },
            ...mapActions({
                setExportRequestId: 'setExportRequestId',
                clearExportRequestId: 'clearExportRequestId'
            }),
            async fetchEnterprises() {
                const { data } = await fetchEnterprisesIdAndNames();
                this.enterprises = data.reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {});
            },
            async fetchExemptions(_, { params: { page, per_page, sort } }) {
                this.fetchController && this.fetchController.abort();
                this.fetchController = new AbortController();
                const { signal } = this.fetchController;

                const loadingTimer = setTimeout(() => {
                    this.loading = true;
                }, 400);

                try {
                    const [field, direction] = sort.split('|');
                    this.sortOrder = [{ field, direction }];

                    const { data } = await fetchReviewedExemptions(
                        { currentPage: page, pageLimit: per_page },
                        { field, direction },
                        this.liveFilters,
                        signal
                    );

                    this.exemptions = data.items;
                    this.pagination.currentPage = page;
                    this.pagination.totalItems = data.totalItems;

                    return {
                        data: {
                            data: data.items,
                            pagination: {
                                page,
                                per_page: this.pagination.itemsPerPage,
                                last_page: Math.ceil(data.totalItems / per_page)
                            }
                        }
                    };
                } finally {
                    clearTimeout(loadingTimer);
                    this.loading = false;
                }
            },
            resetFilters() {
                this.draftFilters = {
                    enterprise: null,
                    employeeName: null,
                    createdAtFrom: null,
                    createdAtTo: null,
                    status: null,
                    submittedAtFrom: null,
                    submittedAtTo: null,
                    validatedAtFrom: null,
                    validatedAtTo: null,
                    rejectedAtFrom: null,
                    rejectedAtTo: null,
                    startDateFrom: null,
                    startDateTo: null,
                    endDateFrom: null,
                    endDateTo: null,
                };
                this.submitFilters();
            },
            async downloadListExport(withAttachments) {
                if (!this.isCanViewExemptions) {
                  return;
                }

                if (withAttachments) {
                    this.exportingListWithAttachments = true;
                } else {
                    this.exportingList = true;
                }
                const { field, direction } = this.sortOrder[0];

                const liveSelection = this.selectAll ? {
                  excludedExemptionIds: Object.entries(this.exemptionSelection).filter(([, selected]) => !selected).map(([id]) => id),
                } : {
                  includedExemptionIds: Object.entries(this.exemptionSelection).filter(([, selected]) => selected).map(([id]) => id),
                };

                await requireListExemptionsExport(
                    { field, direction },
                    { ...this.liveFilters, ...liveSelection },
                    withAttachments
                );

                this.$swal({ title: messageTexts.exportListRequestSubmitted, ...successMessage });

                if (withAttachments) {
                    this.exportingListWithAttachments = false;
                } else {
                    this.exportingList = false;
                }
            },
            async downloadExportItem(id) {
                await downloadExemptionExportFileById(id);
            },
            onFiltersChange() {
                if (this.pagination.currentPage !== 1) {
                    this.pagination.currentPage = 1;
                } else {
                    this.$refs.vuetable.refresh();
                }
            },
            submitFilters() {
                this.liveFilters = { ...this.draftFilters };
            },
            autoCheckExemptions(force = false) {
                if (!this.selectAll) {
                    return;
                }

                this.exemptionSelection = this.exemptions.reduce((acc, { id }) => ({
                    ...acc,
                    [id]: force || (acc[id] ?? true),
                }), force ? {} : { ...this.exemptionSelection });
            },
            updateSelection(exemptionId, isSelected) {
              this.exemptionSelection = { ...this.exemptionSelection, [exemptionId]: isSelected };
            },
            handleSelect(event, exemption) {
                this.selectValue = 'default';
                switch (event.target.value) {
                    case 'export':
                        this.downloadExportItem(exemption.id);
                        break;
                    case 'earlyTermination':
                        this.openEarlyTerminationModal(exemption);
                        break;
                    case 'abort':
                        this.openAbortModal(exemption);
                        break;
                    case 'updatePersonalData':
                        this.$router.push({ name: 'exemption-update', params: { exemptionId: exemption.id } });
                        break;
                }
            },
            openEarlyTerminationModal(exemption) {
                this.earlyTerminationExemption = exemption;
                this.showEarlyTerminationModal = true;
            },
            openAbortModal(exemption) {
                this.abortExemption = exemption;
                this.showAbortModal = true;
            },
            handleAbortSuccess() {
                this.$refs.vuetable.refresh();
                this.$swal({ title: messageTexts.abortSuccess, ...successMessage });
            },
        },
        filters: {
            formatDate
        },
        components: {
            SmoothExpand,
            DatePicker,
            Select,
            Fieldset,
            Sidebar,
            Footer,
            Vuetable,
            Paginator,
            ButtonSpinner,
            PrettyCheck,
            ExemptionListExportRequestsList,
            ExemptionEarlyTermination,
            ExemptionEvaluation
        },
        mixins: [PaginationMixin]
    }
</script>

<style scoped lang="scss">
    .content-wrapper {
        display: flex;

        /deep/ .pretty {
            width: 1.5em;
            margin-right: 0;

            &.p-default {
                input:checked ~ .state label:after {
                    background-color: #007bff !important;
                }

                &.mixed {
                    input:checked ~ .state label:after {
                        background-color: darkgray !important;
                    }
                }
            }

            .state {
                label:before, label:after {
                    width: calc(1.5em + 2px);
                    height: calc(1.5em + 2px);
                }
            }
        }

        .content {
            width: 100%;

            .filters {
                margin-right: 20%;

                @media (max-width: 600px) {
                    margin-right: 0;
                }

                .filter-entry {
                    display: flex;
                    align-items: center;

                    &:not(:first-child) {
                        margin-top: 10px;
                    }

                    &::before {
                        content: '';
                        flex: 1;
                    }

                    label {
                        flex: 0 0 auto;
                        white-space: nowrap;
                        text-align: right;
                        margin-right: 5px;
                    }

                    @media (max-width: 600px) {
                        flex-direction: column;
                        align-items: flex-start;

                        &::before {
                            display: none;
                        }

                        label {
                            text-align: left;
                            margin-right: 0;
                            margin-bottom: 4px;
                        }
                    }

                    .input {
                        flex: 0 0 75%;

                        @media (max-width: 600px) {
                            flex: 0 0 100%;
                            width: 100%;
                        }

                        &--date-range {
                            display: flex;
                            align-items: center;
                            gap: 8px;

                            .date-range-field {
                                display: flex;
                                align-items: center;
                                gap: 8px;

                                .date-range-separator {
                                    font-size: 14px;
                                    white-space: nowrap;
                                    margin-bottom: 0.7rem;
                                }
                            }

                            @media (max-width: 1200px) {
                                flex-wrap: wrap;

                                .date-range-field {
                                    flex: 1 1 100%;
                                    position: relative;
                                    padding-top: 1.4rem;

                                    .date-range-separator {
                                        position: absolute;
                                        top: 0;
                                        left: 0;
                                        margin-bottom: 0;
                                    }

                                    /deep/ .mx-datepicker {
                                        width: 100%;
                                    }
                                }
                            }
                        }
                    }

                }
            }

            .table-pre-header {
                margin: 0 0 10px 0;
                display: flex;
                align-items: center;
                gap: 5px;

                .stats {
                    display: flex;
                    column-gap: 5px;

                    button.nav-button {
                        padding: 0 5px;
                    }
                }

                .button-wrapper {
                    display: flex;
                    column-gap: 10px;
                }

                .spinner-border {
                    margin-top: 3px;
                }
            }

            .filter-buttons-wrapper {
                margin: 10px 0 0 calc(25% + 5px);
            }

            .filter-buttons-wrapper, .submit-buttons-wrapper {
                display: inline-flex;
                column-gap: 10px;
            }

            .afflex-table-content {
                position: relative;

                .select-all {
                    position: absolute;
                    left: 22px;
                    top: 15px;

                    &.mixed {
                      input:checked ~ .state label:after {
                        background: lightgray !important;
                      }
                    }
                }
            }

            /deep/ .vuetable {
                .sort-icon.icon.chevron {
                    display: inline-block;
                    width: 0;
                    height: 0;
                    margin-left: 6px;
                    vertical-align: middle;
                    border-left: 4px solid transparent;
                    border-right: 4px solid transparent;
                    border-top: 6px solid #007bff;
                    opacity: 0.8;
                }

                .sort-icon.icon.chevron.down {
                    transform: rotate(0deg);
                }

                .sort-icon.icon.chevron.up {
                    transform: rotate(180deg);
                }

                .sort-icon.blue {
                    border-top-color: #1e70bf; // correspond au bleu "Semantic UI"
                }

                .vuetable-body {
                    button {
                        padding: 0 20px;
                    }
                }

                .center {
                  text-align: center;
                }
            }
        }
    }
</style>
